/**
 * MicButton — Single-toggle microphone button for voice recording.
 *
 * States:
 *  idle      → muted mic icon
 *  recording → animated pulsing red mic
 *  processing → spinner
 *  speaking  → volume icon
 */

import React from 'react';
import {
  IconButton,
  Tooltip,
  CircularProgress,
  type SxProps,
  type Theme,
} from '@mui/material';
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  VolumeUp as VolumeUpIcon,
} from '@mui/icons-material';
import type { VoiceState } from '../../hooks/useVoiceChat';

interface MicButtonProps {
  voiceState: VoiceState;
  isRecordingSupported: boolean;
  onClick: () => void;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  disabled?: boolean;
  disabledReason?: string;
}

const pulseKeyframes = {
  '@keyframes voicePulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(244,67,54,0.5)' },
    '70%': { boxShadow: '0 0 0 10px rgba(244,67,54,0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(244,67,54,0)' },
  },
};

const tooltipLabel: Record<VoiceState, string> = {
  idle: 'Start voice recording',
  recording: 'Recording… click to send',
  processing: 'Processing…',
  speaking: 'Speaking… (click to stop)',
};

export default function MicButton({
  voiceState,
  isRecordingSupported,
  onClick,
  size = 'medium',
  sx,
  disabled = false,
  disabledReason,
}: MicButtonProps) {
  if (!isRecordingSupported) return null;

  const isActive = voiceState !== 'idle';
  const tooltipTitle = disabled && disabledReason ? disabledReason : tooltipLabel[voiceState];

  const icon = (() => {
    switch (voiceState) {
      case 'recording':
        return <MicIcon />;
      case 'processing':
        return <CircularProgress size={20} color="inherit" />;
      case 'speaking':
        return <VolumeUpIcon />;
      default:
        return <MicOffIcon />;
    }
  })();

  return (
    <Tooltip title={tooltipTitle} arrow>
      <span>
        <IconButton
          onClick={onClick}
          size={size}
          color={isActive ? 'error' : 'default'}
          disabled={disabled}
          sx={{
            ...pulseKeyframes,
            animation: voiceState === 'recording' ? 'voicePulse 1.5s infinite' : 'none',
            transition: 'all 0.2s ease',
            ...sx,
          }}
          aria-label={tooltipTitle}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
}
